import React, { useState, useEffect, useRef } from 'react';
import "./account-picker.css"
import { useTranslation } from 'react-i18next'
import { AccountPickerUserInfo } from './account-picker-user-info';
import { BaseContentBox } from '@pokemon/design.ui.containers.base-content-box';
import { useMediaQuery, useTheme, Modal } from '@mui/material'
import '@emotion/styled'
import '@emotion/react'

export interface AccountPickerProps extends React.HTMLProps<HTMLDivElement> {
  /**
   * Account information of user selected on Account picker
   */
  selectedAccount: AccountPickerAccount
  /**
   * This parameter is used to show the elipses button that lets you switch between accounts or add a new child
   */
  dropdown: boolean
  /**
   * List of children accounts related to the user , it includes own user account
   */
  accounts?: AccountPickerAccount[]
  /**
   * Function that will be trigger when clicking the Add a Child button
   */
  addChild?: () => void
  /**
   * Function that will be trigger when clicking any of the child account buttons. It will return the information of the account that was clicked
   */
  changeAccount?: (currAcc: AccountPickerAccount) => void
  /**
   * We can use it to put our own className on the component
   */
  className?: string;
};

export interface AccountPickerUserInfoProps {
  guid: string
  playerId?: string
  displayName: string
  isButton: boolean
  isCurrent?: boolean
  buttonClick?: (id: string) => void
};

export interface AccountPickerAccount {
  guid: string
  player_id?: string
  first_name?: string
  last_name?: string
  screen_name?: string
  username: string
}

export const displayName = (firstName: string | undefined, lastName: string | undefined, screenName: string | undefined, username: string): string => {
  if (firstName && lastName) {
    return firstName + " " + lastName
  } else if (firstName) {
    return firstName
  } else if (screenName) {
    return screenName
  } else {
    return username
  }
}

export function AccountPicker(props: AccountPickerProps) {

  const { selectedAccount, dropdown, accounts, addChild, changeAccount, className, ...htmlProps } = props

  const [displayChildrenAcc, setDisplayChildrenAcc] = useState(false)
  const [accountsWName, setAccountsWName] = useState<AccountPickerUserInfoProps[]>()
  const { t } = useTranslation()
  const [openModal, setOpenModal] = useState(false)
  const theme = useTheme()
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const ref = useRef(null)

  const onClickOutside = () => {
    setDisplayChildrenAcc(false)
  }

  useEffect(() => {
    if (!smallScreen) {
      const handleClickOutside = (event) => {
        if (ref.current && !ref.current.contains(event.target)) {
          onClickOutside && onClickOutside();
        }
      };
      document.addEventListener('click', handleClickOutside, true);
      return () => {
        document.removeEventListener('click', handleClickOutside, true);
      }
    }
  }, [onClickOutside])


  useEffect(() => {
    if (accounts) {
      let accountsWName: AccountPickerUserInfoProps[] = []
      accounts.forEach(account => {
        let accountWName: AccountPickerUserInfoProps = { guid: account.guid, playerId: account.player_id, displayName: displayName(account.first_name, account.last_name, account.screen_name, account.username), isButton: true }
        accountsWName.push(accountWName)
      })
      setAccountsWName(accountsWName)
    }
  }, [])

  const handleModalOpen = () => {
    setOpenModal(true)
  };

  const handleModalClose = () => {
    setOpenModal(false)
  };

  const handlePickerClick = () => {
    if (dropdown) {
      orderAccounts()
      if (!smallScreen) {
        setDisplayChildrenAcc(!displayChildrenAcc)
      } else {
        if (openModal) {
          setDisplayChildrenAcc(false)
          handleModalClose()
        } else {
          setDisplayChildrenAcc(true)
          handleModalOpen()
        }
      }
    }
  }

  const handleAddChild = () => {
    if (addChild) {
      addChild()
    }
    setDisplayChildrenAcc(false)
    if (smallScreen) {
      handleModalClose()
    }
    focusAccountPicker()
  }

  const orderAccounts = () => {
    if (accountsWName) {
      let sortedAccountsWName = accountsWName.sort((accountA, accountB) => accountA.displayName.localeCompare(accountB.displayName));
      const indexToMove = sortedAccountsWName.findIndex((account) => account.guid === selectedAccount.guid);
      if (indexToMove !== -1) {
        let elementToMove = sortedAccountsWName.splice(indexToMove, 1)[0];
        if (smallScreen) {
          sortedAccountsWName.push(elementToMove);
        } else {
          sortedAccountsWName.unshift(elementToMove);
        }
      }
      setAccountsWName(sortedAccountsWName)
    }
  }

  const handleAccChange = (guid: string) => {
    if (accountsWName && guid !== selectedAccount.guid && accounts) {
      const newCurrAcc = accounts.find(account => account.guid === guid);
      if (newCurrAcc) {
        setDisplayChildrenAcc(false)
        if (changeAccount) {
          changeAccount(newCurrAcc)
        }
        if (smallScreen) {
          handleModalClose()
        }
        focusAccountPicker()
      }
    } else {
      setDisplayChildrenAcc(false)
      if (smallScreen) {
        handleModalClose()
      }
      focusAccountPicker()
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      event.key === "Escape" && isFocuseOnButtons()
    ) {
      setDisplayChildrenAcc(false)
      focusAccountPicker()
    }
  }

  const isFocuseOnButtons = () => {
    let isFocus = false
    if (accounts) {
      const currentFocusElementId = document.activeElement?.id
      if (currentFocusElementId?.includes('account-picker-add-child')) {
        isFocus = true
      } else {
        const idParts = currentFocusElementId?.split('-');
        const idNumber = idParts ? idParts[0] : ''
        const indexOfElement = accounts.findIndex((account) => account.guid === idNumber);
        if (indexOfElement !== -1) {
          isFocus = true
        }
      }
    }
    return isFocus
  }

  const focusAccountPicker = () => {
    const mainElementAccPicker = document.getElementById(`account-picker-main-${selectedAccount.guid}`)
    mainElementAccPicker?.focus()
  }

  return (
    <div ref={ref} className='acc-picker-wrapper' {...htmlProps}>
      <button className={`acc-picker-main  ${className || ""}`} aria-label={t('account_picker_description', 'Click me to switch between accounts or Add a Child account')} id={`account-picker-main-${selectedAccount.guid}`} data-testid={`account-picker-main-${selectedAccount.guid}`} onClick={handlePickerClick}>
        <div className='acc-picker-cmp'>
          <AccountPickerUserInfo displayName={displayName(selectedAccount.first_name, selectedAccount.last_name, selectedAccount.screen_name, selectedAccount.username)} guid={selectedAccount.guid} playerId={selectedAccount.player_id} isButton={false} />
        </div>
        {dropdown && <div className='elipsis' data-testid='elipsis'><img src='https://static.pokemon.com/images/vertical_ellipsis.svg' alt='' /></div>}
      </button>
      {dropdown && displayChildrenAcc && accounts && !smallScreen &&
        <div className='acc-picker-child-accs-main-relative' data-testid={'acc-picker-child-accs-main-relative'} onKeyDown={handleKeyDown} >
          <div className='acc-picker-child-accs-main'>
            <BaseContentBox customWidth={339} className='acc-picker-child-accs-box'>
              <div className='acc-picker-child-accs'>
                {accountsWName?.map((account, index) => {
                  return <AccountPickerUserInfo key={account.guid} displayName={account.displayName} playerId={account.playerId} guid={account.guid} isButton={account.isButton} isCurrent={selectedAccount.guid === account.guid} buttonClick={handleAccChange} />
                })}
              </div>
              <hr className="acc-picker-child-accs-hr" />
              <div className='acc-picker-child-accs-add'>
                <button className='acc-picker-child-accs-add-bttn' id={`account-picker-add-child-${selectedAccount.guid}`} data-testid={`account-picker-add-child-${selectedAccount.guid}`} onClick={handleAddChild}><p className='add-child-button'>{t('add_child', 'Add a Child')}</p></button>
              </div>
            </BaseContentBox>
          </div>
        </div>
      }
      {dropdown && displayChildrenAcc && accounts && smallScreen &&
        <Modal
          open={openModal}
          onClose={handleModalClose}
          aria-describedby="Modal to switch between accounts or add a child account"
          data-testid='acc-picker-modal'
        >
          <div className='acc-picker-child-accs-main-modal' data-testid={'acc-picker-child-accs-main-relative'} onKeyDown={handleKeyDown}>
            <div className='acc-picker-child-accs-modal'>
              {accountsWName?.map((account, index) => {
                return <AccountPickerUserInfo key={account.guid} displayName={account.displayName} guid={account.guid} playerId={account.playerId} isButton={account.isButton} isCurrent={selectedAccount.guid === account.guid} buttonClick={handleAccChange} />
              })}
            </div>
            <hr className="acc-picker-child-accs-hr-modal" />
            <div className='acc-picker-child-accs-add'>
              <button className='acc-picker-child-accs-add-bttn' id={`account-picker-add-child-${selectedAccount.guid}`} data-testid={`account-picker-add-child-${selectedAccount.guid}`} onClick={handleAddChild}><p className='add-child-button'>{t('add_child', 'Add a Child')}</p></button>
            </div>
          </div>
        </Modal>}
    </div>
  );
}